import Company from "@/models/Company";

const getDefaultState = () => ({
    registrationType: null,
    userCompanies: [],
    currentOnboardingPage: '',
    companyName: null,
    companyId: null,
    companyStatus: 'In Progress',
    companyDomain: {},
    companyDomainText: 'Pte. Ltd.',
    financialYearEnd: '',
    primaryBusinessActivity: 0,
    secondaryBusinessActivity: 0,
    capitalCurrency: 0,
    issuedCapitalAmount: '',
    capitalAmount: '',
    totalShare: '',
    reasonToIncorporate: [],
    otherReasonToIncorporate: '',
    sourceOfFund: [],
    otherSourceOfFund: '',
    businessCountries: '',
    registeredAddress: {
        type: '',
        fee: {},
        addressDetails: {
            address: '',
            postalCode: '',
            country: 'Singapore',
        },
    },
    selectedDirectors: [],
    selectedShareHolders: [],
    nationality: [],
    shareType: [],
    isSideBarActionLoading: false,

    // this will update every time when update current company from helpers
    activeCompanyStatus: 'In Progress',
    generatePreIncorporationWithIncorporation: false,
});

export default {
    namespaced: true,
    state: getDefaultState(),

    getters: {
        getAllUserCompanies(state)
        {
            return state.userCompanies;
        },

        getSideBarActionLoadingStatus(state)
        {
            return state.isSideBarActionLoading;
        },

        getCompanyId(state)
        {
            return state.companyId;
        },

        getAllData(state) {
            return state;
        },

        currentOnboardingPage(state) {
            return state.currentOnboardingPage;
        },

        companyName(state) {
            return state.companyName;
        },

        getCurrentCompany(state) {
            return {
                id: state?.companyId,
                name: state.companyName,
                status: state?.companyStatus,
                year_end: state?.financialYearEnd,
                company_type: state?.companyDomain,
            };
        },

        companyDomain(state) {
            return state.companyDomain;
        },

        companyDomainText(state) {
            return state.companyDomainText;
        },

        fullCompanyName(state) {
            return `${state.companyName} ${state.companyDomain.name}`;
        },

        companyStatus(state) {
            return `${state.companyStatus}`;
        },

        getActiveCompanyStatus(state) {
            return state.activeCompanyStatus;
        },

        financialYearEnd(state) {
            return state.financialYearEnd;
        },

        primaryBusinessActivity(state) {
            return state.primaryBusinessActivity;
        },

        secondaryBusinessActivity(state) {
            return state.secondaryBusinessActivity;
        },

        capitalCurrency(state) {
            return state.capitalCurrency;
        },

        issuedCapitalAmount(state) {
            return state.issuedCapitalAmount;
        },

        capitalAmount(state) {
            return state.capitalAmount;
        },

        totalShare(state) {
            return state.totalShare ? parseInt(state.totalShare, 10) : state.totalShare;
        },

        reasonToIncorporate(state) {
            return state.reasonToIncorporate;
        },

        otherReasonToIncorporate(state) {
            return state.otherReasonToIncorporate;
        },

        sourceOfFund(state) {
            return state.sourceOfFund;
        },

        otherSourceOfFund(state) {
            return state.otherSourceOfFund;
        },

        businessCountries(state) {
            return state.businessCountries;
        },

        registeredAddress(state) {
            return state.registeredAddress;
        },

        newDirector(state) {
            return state.newDirector;
        },

        selectedDirectors(state) {
            return state.selectedDirectors;
        },
        selectedShareHolders(state) {
            return state.selectedShareHolders;
        },

        selectedCorporateShareholder(state) {
            if(! state.selectedShareHolders) return [];

            return state.selectedShareHolders
                .filter(shareHolder => shareHolder.shareholderType.value.toLowerCase() === 'corporate')
                .map(({ name }) => name);
        },

        nationality(state)
        {
            return state.nationality;
        },

        registrationType(state)
        {
            return state.registrationType;
        },

        generatePreIncorporationWithIncorporation(state)
        {
            return state.generatePreIncorporationWithIncorporation;
        },

        // fetch any keys from vuex
        get: state => key => state[key],
    },

    mutations: {
        REFRESH_COMPANIES(state, data) {
            state.userCompanies = data;
        },

        UPDATE_USER_COMPANIES(state, data) {
            state.userCompanies = data;
        },

        CLEAR(state) {
            Object.assign(state, getDefaultState());
        },

        ADD_COMPANY(state, data) {
            state.userCompanies.push(data);
        },

        SET_ACTIVE_COMPANY_STATUS(state, status) {
            state.activeCompanyStatus = status;
        },

        UPDATE_CURRENT_COMPANY(state, data) {
            state.companyId = data?.id;
            state.companyName = data?.name;
            state.companyStatus = data?.status;
            state.financialYearEnd = data?.year_end;
            state.companyDomain = data?.company_type;
        },

        UPDATE_SIDE_BAR_ACTION_LOADING_STATUS(state, val) {
            state.isSideBarActionLoading = val;
        },

        UPDATE_CURRENT_ONBOARDING_PAGE(state, val) {
            state.currentOnboardingPage = val;
        },

        UPDATE_COMPANY_NAME(state, val) {
            state.companyName = val;
        },

        UPDATE_COMPANY_DOMAIN(state, val) {
            state.companyDomain = val;
        },

        UPDATE_COMPANY_DOMAIN_TEXT(state, val) {
            state.companyDomainText = val;
        },

        UPDATE_FINANCIAL_YEAR_END(state, val) {
            state.financialYearEnd = val;
        },

        UPDATE_PRIMARY_BUSINESS_ACTIVITY(state, val) {
            state.primaryBusinessActivity = val;
        },

        UPDATE_SECONDARY_BUSINESS_ACTIVITY(state, val) {
            state.secondaryBusinessActivity = val;
        },

        UPDATE_CAPITAL_CURRENCY(state, val) {
            state.capitalCurrency = val;
        },

        UPDATE_ISSUED_CAPITAL_AMOUNT(state, val) {
            state.issuedCapitalAmount = val;
        },

        UPDATE_CAPITAL_AMOUNT(state, val) {
            state.capitalAmount = val;
        },

        UPDATE_TOTAL_SHARE(state, val) {
            state.totalShare = val;
        },

        UPDATE_REASON(state, val) {
            state.reasonToIncorporate = val;
        },

        UPDATE_OTHER_REASON(state, val) {
            state.otherReasonToIncorporate = val;
        },

        UPDATE_SOURCE_OF_FUND(state, val) {
            state.sourceOfFund = val;
        },

        UPDATE_OTHER_SOURCE_OF_FUND(state, val) {
            state.otherSourceOfFund = val;
        },

        UPDATE_BUSINESS_COUNTRIES(state, val) {
            state.businessCountries = val;
        },

        UPDATE_REGISTERED_ADDRESS(state, val) {
            state.registeredAddress = val;
        },

        ADD_SELECTED_DIRECTOR(state, val) {
            state.selectedDirectors.push(val);
        },

        UPDATE_SELECTED_DIRECTOR(state, data) {
            /* eslint-disable */
            state.selectedDirectors.map((beforeDirector, index) => {
                if (
                    beforeDirector.name === data.key.name &&
                    beforeDirector.email === data.key.email
                ) {
                    state.selectedDirectors[index].name = data.value.name;
                    state.selectedDirectors[index].email = data.value.email;
                    state.selectedDirectors[index].contactNumber = data.value.contactNumber;
                }
            });
        },

        REMOVE_SELECTED_DIRECTOR(state, removeDirector) {
            /* eslint-disable */
            state.selectedDirectors.map((director, index) => {
                if (
                director.name === removeDirector.name &&
                director.email === removeDirector.email
                )
                state.selectedDirectors.splice(index, 1);
            });
        },

        ADD_SELECTED_SHAREHOLDER(state, val) {
            state.selectedShareHolders.push(val);
        },

        UPDATE_SELECTED_SHAREHOLDER(state, data) {
            /* eslint-disable */
            state.selectedShareHolders.map((beforeDirector, index) => {
                if (
                    beforeDirector.name === data.key.name &&
                    beforeDirector.email === data.key.email &&
                    beforeDirector.shareholderType === data.key.shareholderType
                ) {
                    state.selectedShareHolders[index].name = data.value.name;
                    state.selectedShareHolders[index].email = data.value.email;
                    state.selectedShareHolders[index].contactNumber = data.value.contactNumber;
                    state.selectedShareHolders[index].type = data.value.type;
                    state.selectedShareHolders[index].shareholderType = data.value.shareholderType;
                    state.selectedShareHolders[index].share = data.value.share;
                    state.selectedShareHolders[index].noOfShares = data.value.noOfShares;
                }
            });
        },

        REMOVE_SELECTED_SHAREHOLDER(state, removeShareHolder) {
            /* eslint-disable */
            state.selectedShareHolders.map((shareholder, index) => {
                if (
                shareholder.name === removeShareHolder.name &&
                shareholder.email === removeShareHolder.email &&
                shareholder.shareholderType === removeShareHolder.shareholderType
                )
                state.selectedShareHolders.splice(index, 1);
            });
        },

        SAVE_NATIONALITY(state, value)
        {
            state.nationality = value;
        },

        SET(state, data)
        {
            state[data.key] = data.value;
        },

        SET_REGISTRATION_TYPE(state, value)
        {
            state.registrationType = value;
        },

        REMOVE(state, key)
        {
            state[key] = null;
        },
    },

    actions: {
        async getAllUserCompanies() 
        {
            const {data} = await Company.getIncorporatedCompanies();
            return data.data;
        },

        async refreshCompanies({ commit })
        {
            const {data} = await Company.all();
            commit('REFRESH_COMPANIES', data);
        },

        storeCapitalAmountAndShare({ commit }, capitalAmount) {
            commit('UPDATE_CAPITAL_AMOUNT', capitalAmount);
            commit('UPDATE_TOTAL_SHARE', capitalAmount);
            commit('UPDATE_ISSUED_CAPITAL_AMOUNT', capitalAmount);
        },

        storeIssuedShareCapitalAmount({ commit }, issuedCapitalAmount) {
            commit('UPDATE_ISSUED_CAPITAL_AMOUNT', issuedCapitalAmount);
        }
    },
};